// maskBurnVideo.ts — browser-only video pass for the PII mask burn-in (EGO-163).
//
// Decode → mask → re-encode one eye, frame for frame. The projection math lives
// in maskBurn.ts (pure, node-testable against the python oracle); this file is
// only the mediabunny/WebCodecs plumbing around it. Like proxy.ts it runs on
// WebCodecs, so it must NEVER be imported from the node entrypoints
// (nodeSource.ts, the parity tests) or `cd core && npm test` breaks.
//
// Spec decisions:
// - This REPLACES the raw left.mp4/right.mp4 — it is the archival copy, not a
//   proxy. The bitrate therefore tracks the SOURCE's own measured bitrate
//   (mediabunny's computePacketStats().averageBitrate over the video track,
//   falling back to bytes*8/duration), never a Quality preset: a privacy
//   re-encode must not also be a quality regression. Generation loss is the
//   price of masking; we pay it once, at ingest, before anything leaves the
//   machine.
//   The request is the source bitrate EXACTLY — no headroom multiplier. The
//   earlier generous config (floored at 12 Mbps, capped at 60, fed the whole
//   *file's* byte rate rather than the track's) made the hardware encoder
//   produce ~2x the source: session 20260825_154144 shipped 115 MB/eye against a
//   58 MB raw. Black mask regions encode cheaper than what they replace, so an
//   exact request lands at or under the source. bitrateMode is pinned to
//   "variable" (mediabunny's default, made explicit) — CBR would spend the full
//   budget on the cheap masked frames.
// - fastStart "in-memory": we are rewriting the file anyway, so the moov may as
//   well come first (the recorder writes it last).
// - Frame count and ORDER are preserved 1:1 with <eye>.timestamps: each decoded
//   sample is re-emitted with its own timestamp/duration, and the loop stops at
//   min(decoded frames, timestamps) exactly like the oracle. Frame index f of the
//   output is still frame index f of <eye>.timestamps — everything downstream
//   (tracking pairing, episode splits, the LeRobot exporter) depends on that.
// - Masking happens in RGBA on a 2D canvas rather than in YUV (the oracle's
//   trick): WebCodecs hands us frames as opaque images, and only the mask's own
//   bounding rect is read back, so the untouched pixels never round-trip.
// - Audio is dropped: the eye videos have no audio track (audio.wav is the audio
//   of record).

import {
  BlobSource,
  BufferTarget,
  CanvasSource,
  Input,
  MP4,
  Mp4OutputFormat,
  Output,
  QTFF,
  VideoSampleSink,
} from "mediabunny";
import { maskForFrame, type EyePlan } from "./maskBurn.ts";

export interface EyeBurnStats {
  /** Frames written — equals framesExpected on a healthy session. */
  frames: number;
  /** min(decoded frames, timestamps) — the oracle's truncation rule. */
  framesExpected: number;
  /** Frames blacked entirely because pose/pairing/projection failed. */
  failedClosed: number;
  /** Mean masked-area % over frames that carry any mask. */
  meanPct: number;
  maxPct: number;
  width: number;
  height: number;
  inputBytes: number;
  outputBytes: number;
  seconds: number;
  /** Measured bitrate of the SOURCE video track (bps) — what we aim to match. */
  sourceBitrate: number;
  /** What the encoder was actually asked for (bps) — == sourceBitrate unless overridden. */
  targetBitrate: number;
}

export interface BurnEyeOpts {
  /** Override the encoder bitrate (bps). Default: match the source exactly. */
  bitrate?: number;
  keyFrameInterval?: number;
  onProgress?: (frac: number) => void;
}

function makeCanvas(w: number, h: number): OffscreenCanvas | HTMLCanvasElement {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(w, h);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  return c;
}

/**
 * Burn `plan`'s masks into one eye's video. Returns the re-encoded mp4 bytes.
 *
 * `plan` must have been built with the VIDEO's dimensions (planEye's width/height
 * overrides) whenever meta.json disagrees with the container — the rasterizer and
 * the canvas have to be the same size or the mask lands in the wrong place.
 */
export async function burnEye(
  video: Blob,
  plan: EyePlan,
  opts: BurnEyeOpts = {},
): Promise<{ bytes: Uint8Array; stats: EyeBurnStats }> {
  const t0 = performance.now();
  const input = new Input({ formats: [MP4, QTFF], source: new BlobSource(video) });
  try {
    const track = await input.getPrimaryVideoTrack();
    if (!track) throw new Error(`${plan.eye}.mp4 has no video track`);
    const w = track.displayWidth;
    const h = track.displayHeight;
    if (w !== plan.cam.w || h !== plan.cam.h) {
      throw new Error(
        `${plan.eye}.mp4 is ${w}x${h} but the mask plan is ${plan.cam.w}x${plan.cam.h}`,
      );
    }
    const durationS = await input.computeDuration();
    const inputBytes = video.size;
    // What the source actually spends. computePacketStats measures the VIDEO
    // TRACK's encoded packets (no container/other-track overhead); bytes*8/duration
    // is the fallback when the demuxer can't produce stats.
    let sourceBitrate = 0;
    try {
      const st = await track.computePacketStats();
      if (Number.isFinite(st?.averageBitrate) && st.averageBitrate > 0) sourceBitrate = st.averageBitrate;
    } catch { /* fall through to the file-level estimate */ }
    if (!(sourceBitrate > 0) && durationS > 0) sourceBitrate = (inputBytes * 8) / durationS;
    sourceBitrate = Math.round(sourceBitrate);
    // Exactly the source — no headroom. Last-resort floor only if we measured
    // nothing at all (unreadable duration AND no packet stats).
    const bitrate = opts.bitrate ?? (sourceBitrate > 0 ? sourceBitrate : 12_000_000);

    const canvas = makeCanvas(w, h);
    const ctx = (canvas as HTMLCanvasElement).getContext("2d", {
      alpha: false,
      willReadFrequently: true,
    }) as CanvasRenderingContext2D;
    if (!ctx) throw new Error("no 2D canvas context for the mask burn");

    const source = new CanvasSource(canvas as any, {
      codec: "avc",
      bitrate,
      bitrateMode: "variable",
      keyFrameInterval: opts.keyFrameInterval ?? 2,
    });
    const output = new Output({
      format: new Mp4OutputFormat({ fastStart: "in-memory" }),
      target: new BufferTarget(),
    });
    output.addVideoTrack(source);
    await output.start();

    const sink = new VideoSampleSink(track);
    const npx = w * h;
    let f = 0;
    let failedClosed = 0;
    let touched = 0;
    let sumPct = 0;
    let maxPct = 0;
    // The oracle truncates to the timestamps count; anything the decoder holds
    // past that has no pose to pair with and would break frame-index parity.
    const limit = plan.frames;
    for await (const sample of sink.samples()) {
      if (f >= limit) { sample.close(); break; }
      try {
        ctx.drawImage(sample.toCanvasImageSource() as CanvasImageSource, 0, 0);
        const fm = maskForFrame(plan, f);
        if (fm.failedClosed) {
          failedClosed++;
          ctx.fillStyle = "#000";
          ctx.fillRect(0, 0, w, h);
        } else if (fm.total > 0) {
          const { x0, y0, x1, y1 } = fm.rect;
          const rw = x1 - x0;
          const img = ctx.getImageData(x0, y0, rw, y1 - y0);
          const px = img.data;
          for (let y = y0; y < y1; y++) {
            const src = y * w;
            const dst = (y - y0) * rw;
            for (let x = x0; x < x1; x++) {
              if (fm.mask[src + x]) {
                const o = (dst + (x - x0)) * 4;
                px[o] = 0; px[o + 1] = 0; px[o + 2] = 0; px[o + 3] = 255;
              }
            }
          }
          ctx.putImageData(img, x0, y0);
        }
        const pct = (100 * fm.total) / npx;
        if (fm.total > 0) { touched++; sumPct += pct; }
        if (pct > maxPct) maxPct = pct;
        await source.add(sample.timestamp, sample.duration);
      } finally {
        sample.close();
      }
      f++;
      opts.onProgress?.(f / limit);
    }
    await output.finalize();
    const buffer = output.target.buffer;
    if (!buffer) throw new Error(`${plan.eye}: mask burn finished without an output buffer`);
    return {
      bytes: new Uint8Array(buffer),
      stats: {
        frames: f,
        framesExpected: limit,
        failedClosed,
        meanPct: touched ? sumPct / touched : 0,
        maxPct,
        width: w,
        height: h,
        inputBytes,
        outputBytes: buffer.byteLength,
        seconds: (performance.now() - t0) / 1000,
        sourceBitrate,
        targetBitrate: bitrate,
      },
    };
  } finally {
    input.dispose();
  }
}

/** Container-reported dimensions of an eye video, so the plan can be built to match. */
export async function videoDimensions(video: Blob): Promise<{ w: number; h: number }> {
  const input = new Input({ formats: [MP4, QTFF], source: new BlobSource(video) });
  try {
    const track = await input.getPrimaryVideoTrack();
    if (!track) throw new Error("no video track");
    return { w: track.displayWidth, h: track.displayHeight };
  } finally {
    input.dispose();
  }
}
